/**
 * SEO-friendly filename composition for downloaded photos.
 *
 * Mirrors the filename pattern the Marketing team has been using by hand
 * across the existing kimonorentalann.com photo library:
 *
 *   {kimono}-{shoot}-{season}-{scene}-{custom?}-{id}.{ext}
 *
 * Examples:
 *   premium-furisode-couple-sakura-sensoji-a8f3.webp
 *   lace-kimono-portrait-sensoji-7c9k.webp
 *   traditional-kimono-asakusa-3xb2.webp
 *
 * All output is lowercase, hyphen-separated, no diacritics, ≤ ~70 chars,
 * and ends with a 4-char base36 hash derived from the Drive fileId so
 * re-downloads produce the same filename.
 *
 * Selection model: ONE slug per category (radio behaviour). When booking
 * tags pre-populate a selection, the user can swap to a different option
 * by clicking another chip in the same category — the previous selection
 * deselects automatically. A single optional `custom` slot lets the user
 * type a free-form keyword for cases the registry doesn't cover.
 */
import type { GalleryTags } from "./api.js";

/** Slug categories the filename builder renders. Ordered top-to-bottom in
 *  the UI and left-to-right in the filename. `custom` is reserved for the
 *  user-typed keyword slot. */
export const SLUG_CATEGORIES = [
  "kimono_tag",
  "shoot_tag",
  "season_tag",
  "scene_tag",
  "custom",
] as const;
export type SlugCategory = (typeof SLUG_CATEGORIES)[number];
type MetadataCategory = Exclude<SlugCategory, "custom">;

/** Single option inside a category — display label paired with the slug
 *  that gets baked into the filename. */
export interface SlugOption {
  /** Label displayed to the user (matches the Airtable enum value where
   *  one exists, otherwise the team's WordPress convention). */
  label: string;
  /** Slug component used in the final filename. */
  slug: string;
}

export interface CategoryDef {
  category: MetadataCategory;
  /** Vietnamese label rendered in the UI. */
  heading: string;
  emoji: string;
  /** All options the user can toggle for this category. */
  options: readonly SlugOption[];
}

/**
 * Catalog of every known option per category. This is the chip set the
 * filename builder UI renders. Editing this list IS the way to add a new
 * kimono tier / location / season — there's no Airtable schema sync.
 *
 * Slug values intentionally don't always match the Airtable enum 1:1 —
 * `Grand premium Furisode` slugs to `luxury-furisode` to match the team's
 * existing folder convention on kimonorentalann.com. Same story for
 * `Samurai styled men kimono` → `samurai-mens-kimono`.
 */
export const CATEGORY_DEFINITIONS: readonly CategoryDef[] = [
  {
    category: "kimono_tag",
    heading: "Kimono",
    emoji: "👘",
    options: [
      { label: "Traditional kimono", slug: "traditional-kimono" },
      { label: "Furisode", slug: "furisode" },
      { label: "Premium Furisode", slug: "premium-furisode" },
      { label: "Grand premium Furisode", slug: "luxury-furisode" },
      { label: "Lace Kimono", slug: "lace-kimono" },
      { label: "Samurai styled men kimono", slug: "samurai-mens-kimono" },
      { label: "(Generic) kimono rental", slug: "kimono-rental" },
    ],
  },
  {
    category: "shoot_tag",
    heading: "Kiểu chụp",
    emoji: "📷",
    options: [
      // "Solo" intentionally omitted: most kimono rental shots are solo
      // women, so the keyword is redundant in the filename and dilutes
      // SEO weight. If a specific shot really needs it, the user can add
      // "solo" via the custom keyword slot.
      { label: "Portrait", slug: "portrait" },
      { label: "Couple", slug: "couple" },
      { label: "Families", slug: "family" },
      { label: "Friends", slug: "friends" },
      { label: "Honeymoon", slug: "honeymoon" },
    ],
  },
  {
    category: "season_tag",
    heading: "Mùa / cảnh đặc biệt",
    emoji: "🌸",
    options: [
      { label: "Sakura", slug: "sakura" },
      { label: "Momiji", slug: "momiji" },
      { label: "Summer", slug: "summer" },
      { label: "Autumn", slug: "autumn" },
      { label: "Night", slug: "night" },
      { label: "Rain", slug: "rain" },
    ],
  },
  {
    category: "scene_tag",
    heading: "Địa điểm",
    emoji: "🏯",
    options: [
      { label: "Sensoji", slug: "sensoji" },
      { label: "Asakusa", slug: "asakusa" },
      { label: "Sumida Park", slug: "sumida-park" },
      { label: "Skytree", slug: "skytree" },
      { label: "Nezu Shrine", slug: "nezu-shrine" },
      { label: "Ueno Park", slug: "ueno-park" },
      { label: "Shioiri Park", slug: "shioiri-park" },
    ],
  },
] as const;

/** Selection state — at most one slug per category, plus an optional
 *  free-form custom keyword. Null means "no value selected for this
 *  category". */
export type FilenameSelection = {
  [K in MetadataCategory]: string | null;
} & {
  custom: string | null;
};

export const EMPTY_SELECTION: FilenameSelection = {
  kimono_tag: null,
  shoot_tag: null,
  season_tag: null,
  scene_tag: null,
  custom: null,
};

/** Vietnamese diacritic stripping. Covers tone marks + Đ/đ. */
function stripDiacritics(input: string): string {
  return input
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D");
}

/** Default slugify: lowercase, strip diacritics, collapse non-alphanum to '-'. */
function slugify(input: string): string {
  return stripDiacritics(input)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-+/g, "-");
}

/** Slugify a free-form user input into the same `[a-z0-9-]+` shape used by
 *  the metadata chips. Returns null when the input collapses to empty. */
export function slugifyCustom(input: string): string | null {
  const slug = slugify(input);
  return slug || null;
}

/**
 * Match a single Airtable tag value to one of the registered slug options
 * for a category. Tries exact label match first, then case-insensitive
 * label match, then slug match. Returns null when nothing fits — caller
 * should not pre-select anything for that category.
 */
export function matchTagToOption(category: MetadataCategory, tagValue: string): SlugOption | null {
  const def = CATEGORY_DEFINITIONS.find((d) => d.category === category);
  if (!def) return null;
  const lower = tagValue.trim().toLowerCase();
  if (!lower) return null;
  const exact = def.options.find((o) => o.label.toLowerCase() === lower);
  if (exact) return exact;
  // Slug match — handles cases where the booking already stores the slug
  const slugLower = slugify(tagValue);
  return def.options.find((o) => o.slug === slugLower) ?? null;
}

/**
 * Pre-populate a `FilenameSelection` from the booking's Airtable tags.
 * For each category, the FIRST tag that matches a registered option is
 * selected. Categories with no matching tag stay null (user picks one).
 * The `custom` slot always starts null.
 */
export function selectionFromTags(tags: GalleryTags | null): FilenameSelection {
  if (!tags) return { ...EMPTY_SELECTION };
  const selection: FilenameSelection = { ...EMPTY_SELECTION };
  for (const def of CATEGORY_DEFINITIONS) {
    for (const value of tags[def.category] ?? []) {
      const option = matchTagToOption(def.category, value);
      if (option) {
        selection[def.category] = option.slug;
        break;
      }
    }
  }
  return selection;
}

/**
 * Toggle a slug within its category — radio behaviour. If the slug is
 * currently selected, deselect it (returns null for that category).
 * Otherwise select it (replacing whatever was previously selected in
 * the same category).
 */
export function toggleCategorySlug(
  selection: FilenameSelection,
  category: MetadataCategory,
  slug: string,
): FilenameSelection {
  const next = { ...selection };
  next[category] = selection[category] === slug ? null : slug;
  return next;
}

/**
 * Derive a stable 4-character base36 ID from a Drive fileId. Two distinct
 * photos very rarely collide in 4 chars (~1.6M slots); within a single
 * booking (≤ ~50 photos) the practical collision rate is essentially zero.
 *
 * Algorithm: FNV-1a 32-bit hash of the fileId, base36-encoded, left-padded
 * to 4 chars. Deterministic — re-downloads produce the same filename.
 */
export function shortHashFromFileId(fileId: string): string {
  let hash = 0x811c9dc5 >>> 0; // FNV offset basis
  for (let i = 0; i < fileId.length; i++) {
    hash = (hash ^ fileId.charCodeAt(i)) >>> 0;
    hash = Math.imul(hash, 0x01000193) >>> 0; // FNV prime
  }
  return hash.toString(36).padStart(4, "0").slice(-4);
}

/**
 * Compose the SEO filename slug (without extension) from a selection
 * + Drive fileId. Returns null when no chip is selected (caller should
 * fall back to the worker's default `photo_{id}` pattern).
 *
 * The slug is guaranteed to satisfy `[a-z0-9]+(-[a-z0-9]+)*` so it
 * survives the worker's `sanitizeFilenameSlug()` whitelist check.
 */
export function composeFilenameSlug(
  selection: FilenameSelection,
  fileId: string,
): string | null {
  const parts: string[] = [];
  for (const def of CATEGORY_DEFINITIONS) {
    const slug = selection[def.category];
    if (slug) parts.push(slug);
  }
  if (selection.custom) parts.push(selection.custom);
  if (parts.length === 0) return null;
  parts.push(shortHashFromFileId(fileId));
  return parts.join("-");
}
